import React, { useEffect, useState } from "react";
import {
  Card,
  useMediaQuery,
  useTheme,
  CircularProgress,
  TextField,
  TablePagination,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import Swal from "sweetalert2";
import Cookies from "js-cookie";

const TENANTS_PAGINATED = gql`
  query TenantsPaginated($page: Int, $limit: Int, $search: String, $onlyArchived: Boolean) {
    getTenantsPaginated(page: $page, limit: $limit, search: $search, onlyArchived: $onlyArchived) {
      tenants {
        _id
        firstName
        lastName
        email
        phoneNumber
        roleId
        isAdmin
        locationIds
        permissionIds
        serviceIds
        domain
        subdomain
        company
      }
      totalCount
      page
      limit
    }
  }
`;

const DELETE_TENANT = gql`
  mutation deleteTenantsMutation($id: ID!, $archivedBy: String) {
    deleteTenantsMutation(id: $id, archivedBy: $archivedBy) {
      _id
      firstName
      lastName
      email
    }
  }
`;

const RESTORE_TENANT = gql`
  mutation restoreTenantMutation($id: ID!, $restoredBy: String) {
    restoreTenantMutation(id: $id, restoredBy: $restoredBy) {
      _id
      firstName
      lastName
      email
    }
  }
`;

export default function TenantsPage() {
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState(""); // raw input, updates every keystroke
  const [debouncedSearch, setDebouncedSearch] = useState(""); // what we actually query with
  const [view, setView] = useState("active"); // "active" | "deleted"

  const navigate = useNavigate();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [fetchTenants, { data, loading }] = useLazyQuery(TENANTS_PAGINATED, {
    fetchPolicy: "network-only",
  });

  const [deleteTenantsMutation] = useMutation(DELETE_TENANT);
  const [restoreTenantMutation] = useMutation(RESTORE_TENANT);

  const tenants = data?.getTenantsPaginated?.tenants || [];
  const totalCount = data?.getTenantsPaginated?.totalCount || 0;

  const runFetch = () => {
    fetchTenants({
      variables: {
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        onlyArchived: view === "deleted",
      },
    });
  };

  // Debounce search input so we don't refetch on every keystroke.
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timeout);
  }, [search]);

  // A new search term or a view switch should always jump back to page 1.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, view]);

  useEffect(() => {
    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, view, debouncedSearch]);

  const handleView = (id) => {
    navigate(`/tenant-details/${id}`);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will deactivate the tenant. You can restore it later from the Deleted view.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteTenantsMutation({
          variables: { id, archivedBy: Cookies.get("SuperAdmin") || null },
        })
          .then(() => {
            Swal.fire("Deleted!", "The tenant has been deactivated.", "success");
            runFetch();
          })
          .catch((error) => {
            Swal.fire(
              "Error!",
              "An error occurred while deleting the tenant.",
              "error"
            );
            console.error("Delete error:", error);
          });
      }
    });
  };

  const handleRestore = (id) => {
    Swal.fire({
      title: "Restore this tenant?",
      text: "The tenant and all of its existing data will become active again.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, restore it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        restoreTenantMutation({
          variables: { id, restoredBy: Cookies.get("SuperAdmin") || null },
        })
          .then(() => {
            Swal.fire("Restored!", "The tenant has been restored.", "success");
            runFetch();
          })
          .catch((error) => {
            Swal.fire(
              "Error!",
              "An error occurred while restoring the tenant.",
              "error"
            );
            console.error("Restore error:", error);
          });
      }
    });
  };

  return (
    <div className="container px-4 py-4">
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <h5 style={{ fontWeight: "bold", fontFamily: "sans-serif" }}>
            All TENANTS
          </h5>
        </div>
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(e, newView) => {
            if (newView) {
              setView(newView);
              setPage(0);
            }
          }}
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="deleted">Deleted</ToggleButton>
        </ToggleButtonGroup>
      </div>

      <div className="mt-3">
        <TextField
          size="small"
          fullWidth
          placeholder="Search by name, email, company, or domain..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <Card className="mt-3" style={{ border: "1px solid #4F5B66", minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? (
          <CircularProgress />
        ) : (
          <table
            className="table table-striped table-bordered"
            style={{ width: "100%", textAlign: "center" }}
          >
            <thead className="thead-light">
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Company</th>
                <th scope="col">Domain</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {tenants.length > 0 ? (
                tenants.map((admin, index) => (
                  <tr
                    key={admin._id || index}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleView(admin._id)}
                    title="View tenant details"
                  >
                    <td style={{ textDecoration: "underline" }}>
                      {admin.firstName || admin.lastName
                        ? `${admin.firstName || ""} ${admin.lastName || ""}`.trim()
                        : "—"}
                    </td>
                    <td>{admin.email}</td>
                    <td>{admin.company || "—"}</td>
                    <td>{admin.domain || "—"}</td>
                    <td>
                      <VisibilityIcon
                        style={{
                          color: "black",
                          cursor: "pointer",
                          marginRight: "10px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(admin._id);
                        }}
                      />
                      {view === "active" ? (
                        <DeleteIcon
                          style={{ color: "red", cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(admin._id);
                          }}
                        />
                      ) : (
                        <RestoreIcon
                          style={{ color: "green", cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestore(admin._id);
                          }}
                        />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Card>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </div>
  );
}
